import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import SuperFetch from 'library/helpers/superFetch';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { Notification } from 'zComponents/atoms';
import { bookingActions } from './slice';
dayjs.extend(utc);
dayjs.extend(timezone);

// 🔥 Get all bookings
export function* getBookings() {
	try {
		// Get the orgID from the selected profile
		const { orgID } = yield select((state) => state.Auth.selectedProfile);

		// Fetch all bookings
		const bookings = yield call(() => SuperFetch.get('/booking'));

		if (bookings.statusCode === 200) {
			yield put(bookingActions.loadBookingsSuccess({ orgID, data: bookings.data }));
		} else {
			throw Error(bookings.error.message || bookings.error || 'Failed to load bookings.');
		}
	} catch (error) {
		yield put(bookingActions.loadBookingsError(error));
	}
}

// 🔥 Create a booking
export function* createBooking({ payload }) {
	try {
		const { data } = payload;
		const createBookingRes = yield call(() => SuperFetch.post('/booking', { ...data, timezone: dayjs.tz.guess() }));

		if (createBookingRes.statusCode === 200) {
			Notification('success', 'Booking Created Successfully!');
			yield put(bookingActions.createBookingSuccess());
		} else {
			Notification('error', 'Failed to create booking.');
			throw Error(createBookingRes.error.message || createBookingRes.error || 'Failed to create booking. Please try again.');
		}
	} catch (error) {
		yield put(bookingActions.createBookingError(error));
		Notification('error', error.message || 'Failed to create booking. Please try again.');
	}
}

// 🔥 After creating a booking, reload the list
export function* createBookingSuccess() {
	yield put(bookingActions.loadBookings());
}

// 🔥 Update a booking
export function* updateBooking({ payload }) {
	try {
		const { bookingID, data } = payload;
		const updateBookingRes = yield call(() => SuperFetch.patch(`/booking/${bookingID}`, data));

		if (updateBookingRes.statusCode === 200) {
			Notification('success', 'Booking updated successfully.');
			yield put(bookingActions.updateBookingSuccess());
		} else {
			Notification('error', 'Failed to update booking.');
			throw Error(updateBookingRes.error.message || updateBookingRes.error || 'Failed to update booking.');
		}
	} catch (error) {
		yield put(bookingActions.updateBookingError(error));
		Notification('error', error.message || 'Something went wrong while updating booking.');
	}
}

// 🔥 After updating a booking, reload the list
export function* updateBookingSuccess() {
	yield put(bookingActions.loadBookings());
}

// 🔥 Delete a booking
export function* deleteBooking({ payload }) {
	try {
		const { bookingID } = payload;
		const deleteBookingRes = yield call(() => SuperFetch.delete(`/booking/${bookingID}`));

		if (deleteBookingRes.statusCode === 200) {
			Notification('success', 'Booking has been permanently deleted.');
			yield put(bookingActions.deleteBookingSuccess());
		} else {
			Notification('error', 'Failed to delete booking.');
			throw Error(deleteBookingRes.error.message || deleteBookingRes.error || 'Failed to delete. Please try again.');
		}
	} catch (error) {
		yield put(bookingActions.deleteBookingError(error));
		Notification('error', error.message || 'Failed to delete. Please try again.');
	}
}

// 🔥 After deleting a booking, reload the list
export function* deleteBookingSuccess() {
	yield put(bookingActions.loadBookings());
}

// 🔥 Export the saga watchers
export default function* bookingsSaga() {
	yield takeLatest(bookingActions.loadBookings, getBookings);
	yield takeLatest(bookingActions.createBooking, createBooking);
	yield takeLatest(bookingActions.createBookingSuccess, createBookingSuccess);
	yield takeLatest(bookingActions.updateBooking, updateBooking);
	yield takeLatest(bookingActions.updateBookingSuccess, updateBookingSuccess);
	yield takeLatest(bookingActions.deleteBooking, deleteBooking);
	yield takeLatest(bookingActions.deleteBookingSuccess, deleteBookingSuccess);
}
